import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { PhoneIcon } from '@heroicons/react/24/outline';
import { baseAPI } from '../config';
import userStore from '../store/userStore';

const AircallCallButton = ({ expedientId, debtorId, onCallSuccess }) => {
  const { user } = userStore();
  const [selectedNumberId, setSelectedNumberId] = useState('');
  const [isCalling, setIsCalling] = useState(false);
  const [isLoadingPhones, setIsLoadingPhones] = useState(false);
  // C2 — solo los números asignados al tenant del expediente (AR → los de la
  // company; judicial/extrajudicial → pool ClaimUp). Los resuelve el backend.
  const [activePhones, setActivePhones] = useState([]);

  const loadPhones = useCallback(async () => {
    if (!expedientId) return;
    setIsLoadingPhones(true);
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const { data } = await axios.get(
        `${baseAPI}/expedients/${expedientId}/sender-numbers?channel=aircall`,
        { headers }
      );
      setActivePhones(data?.data?.numbers || []);
    } catch (err) {
      setActivePhones([]);
      // eslint-disable-next-line no-console
      console.error('Error loading Aircall phones:', err);
    } finally {
      setIsLoadingPhones(false);
    }
  }, [expedientId]);

  useEffect(() => {
    loadPhones();
  }, [loadPhones]);

  // Seleccionar el primer número por defecto si hay números disponibles
  useEffect(() => {
    if (activePhones.length > 0 && !selectedNumberId) {
      setSelectedNumberId(activePhones[0].phone_id);
    }
  }, [activePhones, selectedNumberId]);

  const handleCall = async () => {
    if (!selectedNumberId) {
      toast.error('Por favor, selecciona un número de teléfono');
      return;
    }

    if (!debtorId || !expedientId) {
      toast.error('Información del deudor o expediente no disponible');
      return;
    }

    setIsCalling(true);
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      await axios.post(
        `${baseAPI}/actions/calltodebtor`,
        {
          debtorId,
          expedientId,
          numberId: selectedNumberId,
          user: user,
        },
        { headers }
      );

      toast.success('Llamada iniciada correctamente');
      // Call callback if provided (e.g., to close modal)
      if (onCallSuccess) {
        onCallSuccess();
      }
    } catch (err) {
      const errorMessage = err.response?.data?.error || err.response?.data?.message || 'Error al realizar la llamada';
      toast.error(errorMessage);
      // eslint-disable-next-line no-console
      console.error('Error making call:', err);
    } finally {
      setIsCalling(false);
    }
  };

  if (isLoadingPhones) {
    return (
      <div className='flex items-center justify-center py-4'>
        <div className='animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500'></div>
        <span className='ml-2 text-sm text-slate-500'>Cargando números...</span>
      </div>
    );
  }

  if (activePhones.length === 0) {
    return (
      <div className='p-4 bg-yellow-50 border border-yellow-200 rounded-lg'>
        <p className='text-sm text-yellow-800'>
          No hay ningún número asignado para este expediente. Un administrador debe asignar un número en Configuración.
        </p>
      </div>
    );
  }

  return (
    <div className='flex items-center gap-3'>
      <select
        id='phone-select'
        value={selectedNumberId}
        onChange={e => setSelectedNumberId(e.target.value)}
        className='flex-1 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-slate-900'
      >
        {activePhones.map(phone => (
          <option key={phone.phone_id} value={phone.phone_id}>
            {phone.owned ? '★ ' : ''}{phone.name} - {phone.phone_number}{phone.owned ? ' (empresa)' : ''}
          </option>
        ))}
      </select>
      <button
        type='button'
        onClick={handleCall}
        disabled={isCalling || !selectedNumberId || activePhones.length === 0}
        className='inline-flex items-center justify-center px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap'
      >
        <PhoneIcon className='w-4 h-4 mr-2' />
        {isCalling ? 'Llamando...' : 'Llamar'}
      </button>
    </div>
  );
};

export default AircallCallButton;
